import { mapIfExistsOrThrow } from 'crm-index-visualization-core/core/error/errorUtils';
import { generateHash } from 'crm-index-visualization-core/core/model/hash';
import * as t from 'ts-schema';
import { viewDraftSchema } from './schemas';
import { parseCurried } from './parser';
import { VizConfig } from './viz-config';
import { Segment } from './segment';
import { convertToBackendView } from './legacy-adapters';
import { datesHash } from '../util/storageMangager';
import { ViewMeta } from 'crm-index-visualization-core/core/model/viewMeta';
import { VizSetting } from './vizSetting';
/**
 * Represents a draft of a view, composed of other drafts of its parts.
 *
 */
export class ViewDraft {
  constructor(viewDraft) {
    var _viewDraft$segmentHas, _viewDraft$vizConfigH, _viewDraft$vizSetting;
    this.vizConfigHashesById = {};
    this.vizSettingHashesById = {};
    this.viewId = viewDraft.viewId;
    this.segmentHashOrId = (_viewDraft$segmentHas = viewDraft.segmentHashOrId) !== null && _viewDraft$segmentHas !== void 0 ? _viewDraft$segmentHas : '';
    this.vizConfigHashesById = (_viewDraft$vizConfigH = viewDraft.vizConfigHashesById) !== null && _viewDraft$vizConfigH !== void 0 ? _viewDraft$vizConfigH : {};
    this.vizSettingHashesById = (_viewDraft$vizSetting = viewDraft.vizSettingHashesById) !== null && _viewDraft$vizSetting !== void 0 ? _viewDraft$vizSetting : {};
    this.viewMetaHash = viewDraft.viewMetaHash;
  }

  /**
   * Returns a new ViewDraft with updated properties (immutable update)
   * @param updates - Partial properties to update
   * @returns New ViewDraft instance with updated properties
   */
  with(updates) {
    return new ViewDraft(Object.assign({}, this, updates));
  }
  async getClientCacheKey() {
    return ViewDraft.getClientCacheKey(await this.hash());
  }
  static getClientCacheKey(hash) {
    return `view-${hash}`;
  }
  hash() {
    return generateHash({
      viewId: this.viewId,
      segmentHashOrId: this.segmentHashOrId,
      vizConfigHashesById: this.vizConfigHashesById,
      vizSettingHashesById: this.vizSettingHashesById,
      viewMetaHash: this.viewMetaHash
    });
  }

  /**
   * Loads a view draft from the client cache.
   * @throws {Error} if parsing fails or the view draft is not found in the client cache
   */
  static async fromClientCache(hashOrViewId, clientStorage) {
    var _parsedData$vizConfig, _parsedData$vizSettin;
    const draftJson = await clientStorage.getItem(this.getClientCacheKey(hashOrViewId));
    const parsedData = mapIfExistsOrThrow(draftJson, parseCurried(viewDraftSchema), new Error(`No view draft found in client cache, hash=${hashOrViewId}`));
    return new ViewDraft({
      viewId: parsedData.viewId,
      segmentHashOrId: parsedData.segmentHashOrId,
      vizConfigHashesById: (_parsedData$vizConfig = parsedData.vizConfigHashesById) !== null && _parsedData$vizConfig !== void 0 ? _parsedData$vizConfig : {},
      vizSettingHashesById: (_parsedData$vizSettin = parsedData.vizSettingHashesById) !== null && _parsedData$vizSettin !== void 0 ? _parsedData$vizSettin : {},
      viewMetaHash: parsedData.viewMetaHash
    });
  }

  /**
   * Saves this view draft to the client cache
   * @throws {Error} if saving fails or hash generation fails
   * @throws {Error} if validation fails
   * @returns The hash of the saved view draft
   */
  async toClientCache(clientStorage) {
    // Validate before saving
    t.assert(viewDraftSchema, this);
    const hash = await this.hash();
    const key = ViewDraft.getClientCacheKey(hash);
    await Promise.all([clientStorage.setItem(key, JSON.stringify(this)), datesHash(key, Date.now(), clientStorage)]);
    return hash;
  }

  /**
   * Converts this view draft to a backend view format.
   * This method handles loading the associated segments and visualization configurations
   * from the client cache and converting them to the backend format.
   *
   * @param clientStorage - The client storage instance used for loading cached components
   * @param baseView - The base backend view to use for properties not in the draft
   * @returns A Promise that resolves to a backend view
   * @throws {Error} If any of the component loading or conversion operations fail
   */
  async toBEView(clientStorage, baseView) {
    // Load segment, visualization configurations, and view metadata
    const [segment, vizConfigs, viewMeta, vizSettings] = await Promise.all([Segment.fromClientCache(this.segmentHashOrId, clientStorage).catch(() => null),
    // Load all valid visualization configurations
    Promise.all(Object.entries(this.vizConfigHashesById).filter(([__, hash]) => Boolean(hash)).map(([__, hash]) => VizConfig.fromClientCache(hash, clientStorage))),
    // Load view metadata if present
    this.viewMetaHash ? ViewMeta.fromClientCache(this.viewMetaHash, clientStorage).catch(() => null) : Promise.resolve(null),
    // Load all valid visualization settings
    Promise.all(Object.entries(this.vizSettingHashesById).filter(([__, hash]) => Boolean(hash)).map(([__, hash]) => VizSetting.fromClientCache(hash, clientStorage)))]);

    // Convert to backend view format
    return [segment, viewMeta, ...vizSettings].reduce((view, component) => {
      if (component) {
        return component.toBEView(view);
      }
      return view;
    },
    // TODO:  get rid of this method when removing VizConfig as a concept.
    convertToBackendView(segment, vizConfigs, baseView));
  }
}